import React from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { EffortTooltip } from "@sscs/report/ui";
import { getLevelPrefix } from "@sscs/report/utils";
import {
  ClassificationIcon,
  LevelDiamond,
  RiskStatusIcon,
  TooltipIcon,
  ValueOrPlaceholder,
} from "@sscs/ui";
import { Insight } from "../../../types";
import { getMostSignificantEvaluation } from "../../../utils";
import styles from "./title.module.scss";

const Levels = ({
  levels,
}: {
  levels: Insight["levels"];
}): React.JSX.Element => {
  if (!levels || levels.issue_level === null || levels.scan_level === null) {
    return <ValueOrPlaceholder value={undefined} />;
  }

  if (levels.customized) {
    return <>Causes level customization</>;
  }

  return (
    <>
      Prevents&nbsp;&nbsp;
      <LevelDiamond
        level={levels.issue_level}
        prefix={getLevelPrefix(levels.customized)}
        attained
        size="small"
      />
      &nbsp;&nbsp;Attainment
    </>
  );
};

type TitleProps = { issue: Insight };

const Title = ({ issue }: TitleProps): React.JSX.Element => {
  const riskAssessment = getMostSignificantEvaluation(
    issue.risk_assessment_evaluation,
  );

  return (
    <>
      <div className={styles["issue-wrapper"]}>
        {riskAssessment ? (
          <RiskStatusIcon
            status={riskAssessment.status}
            size="md"
            disabled={false}
          />
        ) : (
          <TooltipIcon size="md" title="Does not cause risk" />
        )}
        <div>
          <div className={styles["issue-name"]}>
            {issue.name}
            {issue.issue_type === "malware" && (
              <ClassificationIcon classification={issue.classification} />
            )}
          </div>
          <div className={styles["risk-label"]}>
            Causes risk: <ValueOrPlaceholder value={riskAssessment?.label} />
            &nbsp;&nbsp;|&nbsp;&nbsp;Introduced:{" "}
            <span title={issue.first_seen}>
              <ValueOrPlaceholder
                value={
                  issue.first_seen
                    ? `${formatDistanceToNowStrict(new Date(issue.first_seen))} Ago`
                    : undefined
                }
              />
            </span>
            &nbsp;&nbsp;|&nbsp;&nbsp;Effort:&nbsp;
            {issue.effort ? (
              <span className={styles.effort}>
                {Math.ceil(issue.effort / 8).toLocaleString()} working days
                <EffortTooltip />
              </span>
            ) : (
              <ValueOrPlaceholder value={undefined} />
            )}
          </div>
        </div>
      </div>
      <div className={styles["safe-levels"]}>
        <b>SAFE levels:</b>&nbsp;
        <Levels levels={issue.levels} />
      </div>
    </>
  );
};

export default Title;
